/**
 * ColorChipEditor — seller-side colour list editor used by the
 * AddProductModal + the SellerPage edit-row.
 *
 * Usage:
 *   <ColorChipEditor
 *     colors={form.colors}
 *     onChange={(list) => setForm(f => ({...f, colors: list}))}
 *     testidPrefix="add-product-color"
 *   />
 *
 * Each colour is `{ name: string, hex: "#RRGGBB" }`. The component:
 *   - shows existing colours as chips with a swatch + label + delete X
 *   - has an inline add-row with a colour input + name input + + button
 *   - rejects duplicate names (case-insensitive) and empty names
 *   - validates hex is #RRGGBB before adding (defaults to the picker's
 *     own value so users can't reach an invalid state)
 *
 * Designed to be drop-in for both create and edit flows.
 */
import React, { useState } from "react";
import { Plus, X, Palette } from "lucide-react";


export default function ColorChipEditor({ colors = [], onChange,
                                          testidPrefix = "color-editor",
                                          compact = false }) {
  const [name, setName] = useState("");
  const [hex, setHex] = useState("#D4FF00");

  const add = () => {
    const n = name.trim();
    if (!n) return;
    const lower = n.toLowerCase();
    if (colors.some(c => c.name.toLowerCase() === lower)) return; // dedupe
    onChange([...colors, { name: n, hex }]);
    setName("");
  };
  const remove = (i) => onChange(colors.filter((_, idx) => idx !== i));

  return (
    <div className="border border-[#3D2845] rounded-xl p-3 bg-[#261534]/50"
      data-testid={`${testidPrefix}-section`}>
      <div className="flex items-center gap-2 mb-3">
        <Palette size={13} className="text-[#FF007F]"/>
        <span className="font-bold uppercase tracking-widest text-[10px]">Colours</span>
        <span className="text-[#A1A1AA] text-[10px] normal-case font-normal">
          — buyers pick one swatch before they checkout
        </span>
      </div>

      {/* Existing chips */}
      {colors.length > 0 && (
        <div className="flex flex-wrap gap-1.5 mb-3" data-testid={`${testidPrefix}-chips`}>
          {colors.map((c, i) => (
            <span key={`${c.name}-${i}`}
              data-testid={`${testidPrefix}-chip-${i}`}
              className="inline-flex items-center gap-1.5 pl-1 pr-1.5 py-1 rounded-full bg-[#1A0F1F] border border-[#3D2845] text-[11px]">
              <span className="w-4 h-4 rounded-full border border-[#3D2845]"
                style={{ background: c.hex }}/>
              <span className="font-bold">{c.name}</span>
              <span className="text-[9px] text-[#A1A1AA] font-mono-display">{c.hex.toUpperCase()}</span>
              <button type="button" onClick={() => remove(i)}
                data-testid={`${testidPrefix}-remove-${i}`}
                className="ml-1 w-4 h-4 rounded-full bg-[#3D2845] hover:bg-[#FF007F] text-white flex items-center justify-center">
                <X size={9}/>
              </button>
            </span>
          ))}
        </div>
      )}

      {/* Add row */}
      <div className={`grid ${compact ? "grid-cols-[40px_1fr_auto]" : "grid-cols-[50px_1fr_auto]"} gap-2 items-center`}>
        <input type="color" value={hex} onChange={e => setHex(e.target.value)}
          data-testid={`${testidPrefix}-hex-input`}
          className={`${compact ? "h-8" : "h-9"} w-full rounded cursor-pointer bg-transparent border border-[#3D2845]`}
          aria-label="Pick colour"/>
        <input value={name} onChange={e => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="Colour name (e.g. Burgundy)"
          data-testid={`${testidPrefix}-name-input`}
          className={`input-dark ${compact ? "text-xs" : "text-sm"} w-full`}/>
        <button type="button" onClick={add} disabled={!name.trim()}
          data-testid={`${testidPrefix}-add-btn`}
          className="btn-secondary text-[10px] uppercase tracking-widest inline-flex items-center gap-1 disabled:opacity-40">
          <Plus size={11}/> Add
        </button>
      </div>
    </div>
  );
}
